import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { seatStyle } from '../helpers'
import type { BoardProps } from './types'

/* ------------------------------------------------------------------ rock paper scissors */

type Pick = 'rock' | 'paper' | 'scissors'
interface RpsView {
  picks: (Pick | 'locked' | null)[]
  wins: number[]
  history: { a: Pick; b: Pick; winner: number | null }[]
}
const ICON: Record<Pick, string> = { rock: '✊', paper: '✋', scissors: '✌️' }
const PICKS: Pick[] = ['rock', 'paper', 'scissors']

export function Rps({ view, seat, over, busy, send, players, people }: BoardProps<RpsView>) {
  const mine = seat !== null ? view.picks[seat] : null
  const name = (s: number) => (s === seat ? 'You' : (people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'))
  return (
    <div className="mx-auto grid w-full max-w-md gap-4 text-center">
      <p className="flex items-center justify-center gap-6 text-lg font-semibold">
        {[0, 1].map((s) => (
          <span key={s} className={cn('flex items-center gap-2', seatStyle(s).text)}>
            {name(s)} <span className="num rounded-md bg-muted px-2 py-0.5 tabular-nums text-foreground">{view.wins[s]}</span>
          </span>
        ))}
      </p>
      <p className="text-xs text-text-muted">First to two rounds. Picks are shown together, so nobody can copy.</p>
      <div className="flex items-center justify-center gap-3">
        {PICKS.map((p) => (
          <Button
            key={p}
            type="button"
            variant={mine === p ? 'default' : 'outline'}
            disabled={seat === null || over || busy || Boolean(mine)}
            onClick={() => send({ pick: p })}
            className="h-20 w-24 flex-col gap-1 text-3xl"
            aria-label={p}
          >
            <span aria-hidden>{ICON[p]}</span>
            <span className="text-xs font-medium capitalize">{p}</span>
          </Button>
        ))}
      </div>
      <p className="min-h-5 text-sm text-text-secondary" aria-live="polite">
        {over ? '' : mine && mine !== 'locked' ? 'Locked in. Waiting for them…' : seat === null ? (view.picks.some(Boolean) ? 'One of them has picked.' : 'Waiting for both to pick.') : 'Make your pick.'}
      </p>
      {view.history.length ? (
        <ol className="grid gap-1 text-sm">
          {view.history.map((h, i) => (
            <li key={i} className="flex items-center justify-center gap-3 rounded-md bg-muted px-3 py-1">
              <span className="text-xs text-text-muted">Round {i + 1}</span>
              <span className="text-xl" aria-label={h.a}>{ICON[h.a]}</span>
              <span className="text-xs text-text-muted">vs</span>
              <span className="text-xl" aria-label={h.b}>{ICON[h.b]}</span>
              <span className={cn('w-20 text-xs font-medium', h.winner === null ? 'text-text-muted' : seatStyle(h.winner).text)}>{h.winner === null ? 'Tie, again' : `${name(h.winner)} won`}</span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ battleship */

interface BoardView {
  shots: { cell: number; hit: boolean }[]
  sunk: number[][]
  ships: number[][] | null
}
interface BattleView {
  boards: [BoardView, BoardView]
  turn: number
  last: { by: number; cell: number; result: 'miss' | 'hit' | 'sunk' } | null
  fleet: number[]
}

function Waters({ board, fire, label, last }: { board: BoardView; fire: ((cell: number) => void) | null; label: string; last: number | null }) {
  const shots = new Map(board.shots.map((s) => [s.cell, s.hit]))
  const ships = new Set((board.ships ?? []).flat())
  const sunk = new Set(board.sunk.flat())
  return (
    <div className="mx-auto grid w-full max-w-sm gap-1">
      <p className="text-xs font-semibold text-text-secondary">{label}</p>
      <div className="grid grid-cols-10 gap-px rounded-md border border-border bg-border-strong p-px" role="grid" aria-label={label}>
        {Array.from({ length: 100 }, (_, i) => {
          const shot = shots.has(i)
          const hit = shots.get(i) === true
          return (
            <button
              key={i}
              type="button"
              disabled={!fire || shot}
              onClick={() => fire?.(i)}
              aria-label={`Row ${Math.floor(i / 10) + 1}, column ${(i % 10) + 1}${shot ? (hit ? ', hit' : ', miss') : ''}`}
              className={cn(
                'relative grid aspect-square place-items-center text-[0.6rem] leading-none',
                ships.has(i) ? 'bg-brand/35' : 'bg-card',
                sunk.has(i) && 'bg-destructive/20',
                fire && !shot && 'hover:bg-accent',
                last === i && 'ring-2 ring-inset ring-warning',
              )}
            >
              {shot ? hit ? <span className="size-[60%] rounded-full bg-destructive" /> : <span className="size-[28%] rounded-full bg-text-muted" /> : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export function Battleship({ view, seat, over, busy, send, players, people }: BoardProps<BattleView>) {
  const name = (s: number) => people.get(players[s] ?? '')?.displayName.split(' ')[0] ?? 'Player'
  const play = seat !== null && seat === view.turn && !over && !busy
  const lastAt = (board: number) => (view.last && view.last.by !== board ? view.last.cell : null)
  const sunkCount = (b: BoardView) => b.sunk.length
  if (seat === null) {
    return (
      <div className="grid gap-4 sm:grid-cols-2">
        {[0, 1].map((s) => (
          <Waters key={s} board={view.boards[s as 0 | 1]} fire={null} label={`${name(s)}'s waters · ${sunkCount(view.boards[s as 0 | 1])} of ${view.fleet.length} ships sunk`} last={lastAt(s)} />
        ))}
      </div>
    )
  }
  const enemy = 1 - seat
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Waters
          board={view.boards[enemy as 0 | 1]}
          fire={play ? (cell) => send({ cell }) : null}
          label={`${name(enemy)}'s waters · ${sunkCount(view.boards[enemy as 0 | 1])} of ${view.fleet.length} ships sunk${play ? ' · fire!' : ''}`}
          last={lastAt(enemy)}
        />
        <Waters board={view.boards[seat as 0 | 1]} fire={null} label={`Your fleet · ${sunkCount(view.boards[seat as 0 | 1])} sunk`} last={lastAt(seat)} />
      </div>
      {view.last ? (
        <p className="text-center text-sm text-text-secondary" aria-live="polite">
          {view.last.by === seat ? 'You' : name(view.last.by)} fired at row {Math.floor(view.last.cell / 10) + 1}, column {(view.last.cell % 10) + 1}:{' '}
          <span className={cn('font-semibold', view.last.result === 'miss' ? 'text-text-muted' : 'text-destructive')}>{view.last.result === 'miss' ? 'a miss' : view.last.result === 'hit' ? 'a hit!' : 'a ship sunk!'}</span>
        </p>
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ memory */

interface MemoryView {
  cards: { symbol: string | null; owner: number | null; up: boolean }[]
  turn: number
  scores: number[]
  hideIn: number | null
}

export function Memory({ view, seat, over, busy, send, refresh }: BoardProps<MemoryView>) {
  const play = seat !== null && seat === view.turn && !over && !busy && view.hideIn === null
  const hideIn = view.hideIn
  // A mismatched pair turns back by itself; look again the moment it should have.
  useEffect(() => {
    if (hideIn === null) return
    const id = window.setTimeout(refresh, hideIn + 80)
    return () => window.clearTimeout(id)
  }, [hideIn, refresh])
  return (
    <div className="mx-auto grid w-full max-w-sm grid-cols-4 gap-2" role="grid" aria-label="Memory cards">
      {view.cards.map((c, i) => (
        <button
          key={i}
          type="button"
          disabled={!play || c.symbol !== null}
          onClick={() => send({ card: i })}
          aria-label={c.symbol ? `Card ${i + 1}: ${c.symbol}` : `Card ${i + 1}, face down`}
          className={cn(
            'grid aspect-square place-items-center rounded-xl border-2 text-3xl transition-transform',
            c.symbol === null ? 'border-brand-dark bg-brand text-text-inverse hover:bg-brand-dark' : c.owner !== null ? cn('border-transparent', seatStyle(c.owner).soft, 'ring-2', seatStyle(c.owner).ring) : 'border-warning bg-card',
          )}
        >
          {c.symbol ?? <span className="text-xl opacity-70" aria-hidden>?</span>}
        </button>
      ))}
    </div>
  )
}
